import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../context/AuthContext';

export type PlanType = 'free' | 'pro' | 'agency';

export interface UserPlan {
  plan: PlanType;
  profileLimit: number;
  generationLimit: number | null;
  creditLimit: number;
  adsPerBatch: number;
}

const PLAN_CONFIG: Record<PlanType, UserPlan> = {
  free:   { plan: 'free',   profileLimit: 1,   generationLimit: null, creditLimit: 25,   adsPerBatch: 1 },
  pro:    { plan: 'pro',    profileLimit: 3,   generationLimit: null, creditLimit: 250,  adsPerBatch: 3 },
  agency: { plan: 'agency', profileLimit: 999, generationLimit: null, creditLimit: 1000, adsPerBatch: 6 },
};

export const CREDIT_COSTS = {
  GENERATE_AD: 10,
  REGENERATE_IMAGE: 5,
  EDIT_IMAGE: 2,
  REWRITE_COPY: 2,
  FREE_EDITS_ALLOWED: 2,
} as const;

export const CREDIT_TOP_UPS = [
  { id: 'starter', label: 'Starter Pack', credits: 100,  price: '$12', priceRaw: 12, saving: null },
  { id: 'growth',  label: 'Growth Pack',  credits: 300,  price: '$35', priceRaw: 35, saving: 'Popular' },
  { id: 'power',   label: 'Power Pack',   credits: 1000, price: '$99', priceRaw: 99, saving: 'Best Value' },
] as const;

export { PLAN_CONFIG };

/** Available image batch sizes per plan */
export const PLAN_BATCH_OPTIONS: Record<PlanType, (1 | 3 | 6)[]> = {
  free:   [1],
  pro:    [1, 3],
  agency: [1, 3, 6],
};

export function useUserPlan() {
  const { user } = useAuth();
  const [planData, setPlanData] = useState<UserPlan>(PLAN_CONFIG.free);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) {
      setLoading(false);
      return;
    }
    supabase
      .from('profiles')
      .select('plan')
      .eq('id', user.id)
      .maybeSingle()
      .then(({ data }) => {
        const plan = (data?.plan ?? 'free') as PlanType;
        setPlanData(PLAN_CONFIG[plan] ?? PLAN_CONFIG.free);
        setLoading(false);
      });
  }, [user]);

  return { ...planData, loading };
}
